import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { 
  User,
  Trophy, 
  Calendar, 
  MapPin, 
  Award,
  TrendingUp,
  Star,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  BarChart3,
  Target,
  Medal,
  Activity,
  Clock,
  Briefcase,
  GraduationCap,
  Users
} from 'lucide-react';
import { coachesApi } from '../../services/api';
import { useSiteConfig } from '../../hooks/useSiteConfig';
import Breadcrumb from '../Common/Breadcrumb';

const CoachDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>(); // This will actually be the slug now
  const siteConfig = useSiteConfig();
  const [expandedSection, setExpandedSection] = useState<string | null>('career');

  // Fetch coach details (using slug)
  const { data: coach, isLoading, error } = useQuery({
    queryKey: ['coach', id],
    queryFn: async () => {
      if (!id) throw new Error('Coach slug is required');
      return await coachesApi.getBySlug(id); // Using getBySlug instead of getById
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-400">Loading coach details...</p>
        </div>
      </div>
    );
  }

  if (error || !coach) {
    return (
      <div className="text-center py-12">
        <User className="w-16 h-16 text-gray-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">Coach Not Found</h3>
        <p className="text-gray-400 mb-6">We couldn't find details for this coach.</p>
        <Breadcrumb
          items={[{ label: 'All Coaches', path: '/coaches' }]}
          showBackButton={true}
          backButtonText="Back to Coaches"
          backButtonPath="/coaches"
        />
      </div>
    );
  }

  // Extract coach info from API response
  const coachName = `${coach.person?.first_name || ''} ${coach.person?.last_name || ''}`.trim();
  const title = coach.title || 'Coach';
  const specialization = coach.specialization || 'General';
  const yearsExperience = coach.years_experience || 0;
  const isHeadCoach = coach.is_head_coach || false;
  const age = coach.person?.age || 'N/A';
  const birthDate = coach.person?.birth_date || 'N/A';
  const college = coach.person?.college || 'N/A';
  const currentTeam = coach.current_team;
  const coachingHistory = coach.coaching_history || [];
  const coachingRecord = coach.coaching_record || { wins: 0, losses: 0, win_percentage: 0 };

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Breadcrumb Navigation */}
        <Breadcrumb
          items={[
            { label: 'All Coaches', path: '/coaches' },
            { label: coachName || 'Coach Details', isActive: true }
          ]}
        />

        {/* Coach Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-8 mb-8"
        >
          <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
            {/* Coach Avatar */}
            <div className="relative">
              <div className="w-32 h-32 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                {isHeadCoach ? (
                  <Trophy className="w-12 h-12 text-primary" />
                ) : (
                  <Briefcase className="w-12 h-12 text-secondary" />
                )}
              </div>
              {coach.is_active && (
                <div className="absolute bottom-0 right-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <Activity className="w-4 h-4 text-white" />
                </div>
              )}
            </div>

            {/* Coach Info */}
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-4xl font-bold text-white mb-2">{coachName}</h1>
              {currentTeam && (
                <div className="text-lg text-gray-300 mb-2">
                  {currentTeam.name}
                </div>
              )}
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-gray-400 mb-4">
                <span className="flex items-center gap-1">
                  <Briefcase className="w-4 h-4" />
                  {title}
                </span>
                {specialization && (
                  <span className="flex items-center gap-1">
                    <Target className="w-4 h-4" />
                    {specialization}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Age: {age}
                </span>
              </div>
              
              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">
                    {yearsExperience}
                  </div>
                  <div className="text-xs text-gray-500">Years Experience</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-secondary">
                    {isHeadCoach ? 'Head' : 'Assistant'}
                  </div>
                  <div className="text-xs text-gray-500">Coach Type</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent">
                    {coachingRecord.wins || 0}-{coachingRecord.losses || 0}
                  </div>
                  <div className="text-xs text-gray-500">Career Record</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">
                    {coach.is_active ? 'Active' : 'Inactive'}
                  </div>
                  <div className="text-xs text-gray-500">Status</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Personal Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card p-6 mb-6"
        >
          <button
            onClick={() => toggleSection('personal')}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-primary" />
              <h2 className="text-xl font-bold text-white">Personal Information</h2>
            </div>
            {expandedSection === 'personal' ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          
          {expandedSection === 'personal' && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              <div>
                <div className="text-sm text-gray-500 mb-1">Full Name</div>
                <div className="text-white">{coachName}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">Birth Date</div>
                <div className="text-white">{birthDate}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">Birth Place</div>
                <div className="text-white">
                  {coach.person?.birth_city || 'N/A'}
                  {coach.person?.birth_state && `, ${coach.person.birth_state}`}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">College</div>
                <div className="text-white">{college}</div>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Career Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-card p-6 mb-6"
        >
          <button
            onClick={() => toggleSection('career')}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-3">
              <Trophy className="w-5 h-5 text-primary" />
              <h2 className="text-xl font-bold text-white">Career Information</h2>
            </div>
            {expandedSection === 'career' ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          
          {expandedSection === 'career' && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-6 space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-500 mb-1">Current Title</div>
                  <div className="text-white">{title}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Specialization</div>
                  <div className="text-white">{specialization}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Years of Experience</div>
                  <div className="text-white">{yearsExperience} years</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Coach Type</div>
                  <div className="text-white">{isHeadCoach ? 'Head Coach' : 'Assistant Coach'}</div>
                </div>
              </div>
              
              {coach.previous_teams && coach.previous_teams.length > 0 && (
                <div>
                  <div className="text-sm text-gray-500 mb-2">Previous Teams</div>
                  <div className="flex flex-wrap gap-2">
                    {coach.previous_teams.map((team: any, index: number) => (
                      <span key={index} className="badge badge-secondary text-xs">
                        {team.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {coach.championships_won && coach.championships_won > 0 && (
                <div>
                  <div className="text-sm text-gray-500 mb-1">Championships Won</div>
                  <div className="text-white">{coach.championships_won}</div>
                </div>
              )}
            </motion.div>
          )}
        </motion.div>

        {/* Coaching History */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-card p-6 mb-6"
        >
          <button
            onClick={() => toggleSection('history')}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-primary" />
              <h2 className="text-xl font-bold text-white">Coaching History</h2>
            </div>
            {expandedSection === 'history' ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          
          {expandedSection === 'history' && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-6"
            >
              {coachingHistory && coachingHistory.length > 0 ? (
                <div className="space-y-4">
                  {coachingHistory.map((history: any, index: number) => (
                    <div key={index} className="border-l-2 border-primary pl-4">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-white font-semibold">{history.team_name}</span>
                        {history.is_active && (
                          <span className="badge badge-success text-xs">Current</span>
                        )}
                      </div>
                      <div className="text-sm text-gray-400">
                        {history.start_year} - {history.end_year || 'Present'} • {history.title}
                      </div>
                      {history.is_head_coach && (
                        <div className="text-xs text-primary mt-1">
                          Head Coach
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400">No coaching history available.</p>
              )}
            </motion.div>
          )}
        </motion.div>

        {/* Achievements */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="glass-card p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <Award className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-bold text-white">Achievements & Awards</h2>
          </div>
          {coach.awards && coach.awards.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {coach.awards.map((award: any, index: number) => (
                <span key={index} className="badge badge-primary text-xs">
                  {award.name} ({award.year})
                </span>
              ))}
            </div>
          ) : (
            <p className="text-gray-400">
              Achievements and awards will be displayed here once available.
            </p>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default CoachDetail;